// ---- the harness ----
// What a script drives a game by, on top of the ?dbg handle (exposeDebug,
// loop.ts): the first touch, the clock, game time a frame at a time, the round
// played through, and a character held where it is put. Every one of these
// was a recipe in notes outside the repo, rebuilt by hand in each session that
// needed it; this is the one copy, and docs/verify.md says which to use when
// and what each cannot do. None of it exists without ?dbg on the address.
import * as THREE from 'three';
import {chips} from './chips';
import {facesOf} from './letters';
import {round,tap} from './round';
import {cam} from './scene';

// The clock the game reads: frame() takes its dt from performance.now, and so
// does everything timed in game time (the round's beats, the board's steps).
// Frozen, game time moves only when step() or run() moves it, so a frame is
// exact and repeatable and a soak runs as fast as the machine ticks. Thawed,
// it runs on from wherever it got to — never backwards, since one negative dt
// makes the camera's eased pitch NaN for good. What a frozen clock does NOT
// move is a setTimeout: the serve after a finished word and the next deal are
// on those, on the real clock, so a whole round is played thawed.
const realNow=performance.now.bind(performance);
let fake:number|null=null, offset=0, installed=false;
const now=()=>fake??realNow()+offset;
export const clock={
  get frozen(){return fake!==null;},
  freeze(){if(!installed){performance.now=now;installed=true;}if(fake===null)fake=now();return fake;},
  thaw(){if(fake!==null){offset=fake-realNow();fake=null;}},
  step(ms:number){if(fake===null)throw new Error('clock.step: freeze() first');fake+=ms;return fake;},
};

// The loop's frame, bare (a throw reaches the caller), and when it last ran.
interface Loop{frame:()=>void;lastFrame:()=>number}
let loop:Loop={frame:()=>{},lastFrame:()=>-1e9};
export function bindHarness(l:Loop){loop=l;}
// A breath that a hidden pane does not throttle, unlike a timer.
const breath=()=>new Promise<void>(r=>{const c=new MessageChannel();c.port1.onmessage=()=>r();c.port2.postMessage(0);});
const sleep=(ms:number)=>new Promise<void>(r=>setTimeout(r,ms));
// Game time, ms of it. Frozen: stepped exactly, one frame per step, with a
// breath every 500 so a long run does not hang the page. Thawed: waited out,
// and the browser's own loop draws the frames; one is ticked from here only
// while that loop has gone quiet (a hidden pane stops it), so no frame is
// drawn twice and the game still moves when nobody is looking.
// `each` is called after every frame — a soak's probe, reading its counters
// off the handle while the positions are exactly as that frame left them.
// While a frozen run is going, the browser's own loop is told to skip: a
// breath lets it in, and a frame of its own on a clock that has not moved is
// a brain call with dt 0 and a draw of the random stream that the soak did
// not make.
let running=false;
export const busy=()=>running;
export async function run(ms:number,step=1000/60,each?:(t:number)=>void){
  if(fake!==null){running=true;try{let n=0;for(let left=ms;left>0;left-=step){fake+=Math.min(step,left);loop.frame();each?.(fake);if(++n%500===0)await breath();}}finally{running=false;}return;}
  const end=now()+ms;
  while(now()<end){
    if(now()-loop.lastFrame()>40){loop.frame();each?.(now());const t0=now();while(now()-t0<16)await breath();}
    else await sleep(16);
  }
}
// Whether a frame is drawn at all. Everything that moves is decided before the
// render and nothing reads the picture back, so a soak that only wants the
// counters turns the drawing off and runs ten times faster on a software GPU;
// the one picture it wants at the end is a tick() with it back on.
export const draw={on:true};
// How big something stands, posed as it is this frame: its box's size, in the
// field's units. The soak reads each animal's height against its spec with it.
const box=new THREE.Box3(), sz=new THREE.Vector3();
export function size(o:THREE.Object3D){return box.setFromObject(o).getSize(sz).clone();}

// The first touch: what unlocks audio, and so the hello, and so the round.
export function touch(){document.dispatchEvent(new PointerEvent('pointerdown',{bubbles:true}));}

// One move of the round, as a child would make it: the chip the word wants
// next, tapped where it is on the screen — or, with no chips up, whatever the
// game's own board asks for (setPlay: the tracing board's next layer). False
// when nothing is waiting on a finger.
const v=new THREE.Vector3();
let alt:(()=>boolean)|null=null;
export function setPlay(f:()=>boolean){alt=f;}
export function play():boolean{
  const w=round.target?.w;
  if(w&&round.dealt){
    const f=facesOf(w)[round.step], c=f===undefined?undefined:chips.find(c=>c.face===f&&!c.dying);
    if(c){v.copy(c.position).project(cam);tap((v.x+1)/2*innerWidth,(1-v.y)/2*innerHeight,now());return true;}
  }
  return alt?alt():false;
}

// A character held at a spot while its brain goes on thinking it moves it: the
// x and z of its position answer with the pin and swallow every write. For a
// pinned scenario — her on his short way round the table — rather than a
// random soak that hits it once an hour. unpin() gives the position back.
export function pin(o:THREE.Object3D,x:number,z:number){
  const p=o.position;
  Object.defineProperty(p,'x',{get:()=>x,set:()=>{},configurable:true});
  Object.defineProperty(p,'z',{get:()=>z,set:()=>{},configurable:true});
}
export function unpin(o:THREE.Object3D){
  const p=o.position, x=p.x, z=p.z, q=p as unknown as Record<string,number>;
  delete q['x']; delete q['z']; p.x=x; p.z=z;
}
